import { Button } from "@/components/ui/button";
import { useAuthProviders } from "@/lib/api";
import { AUTH_RETURN_TO_KEY, isSafeInternalPath, markOauthFlowStarted } from "@/lib/auth-redirect";
import { logger } from "@/lib/logger";

type Provider = "google";

interface OAuthButtonsProps {
  /**
   * Internal path to return to after the provider round-trip. Stashed in
   * localStorage (the fragment/callback flow can't carry app state) and
   * validated on the way back out in `resolveAuthRedirect`.
   */
  returnTo?: string;
}

/**
 * The Google sign-in button plus the "or" divider that separates it from the
 * email form. The button is rendered only when Supabase reports Google enabled
 * (via `useAuthProviders`), so a provider toggled off in the dashboard drops out
 * of the UI instead of erroring on click.
 *
 * Fails CLOSED: while the query is pending, on its 502 (Supabase unreachable),
 * or when no provider is enabled, this renders nothing at all (divider
 * included) so no parent surface shows an orphan "or" or a dead button; the
 * email form below remains the reliable path. It never defaults to rendering
 * the button on error.
 *
 * Clicking the button leaves the SPA entirely via a full navigation to the
 * backend's `/api/auth/oauth/{provider}` redirect endpoint, so that step is a
 * plain `window.location.assign`, not an SDK/fetch call.
 */
export function OAuthButtons({ returnTo }: OAuthButtonsProps) {
  // Only read `data`; the query captures any error internally, so nothing here
  // throws or bubbles an error state up to the parent surface.
  const { data } = useAuthProviders();
  const providers = data?.providers ?? [];

  function start(provider: Provider) {
    // Always set OR clear the key: a stale value from an earlier, abandoned
    // OAuth attempt must never hijack this (or a later) sign-in's redirect.
    try {
      if (isSafeInternalPath(returnTo)) {
        localStorage.setItem(AUTH_RETURN_TO_KEY, returnTo);
      } else {
        localStorage.removeItem(AUTH_RETURN_TO_KEY);
      }
    } catch {
      // Non-fatal: without the stash we fall back to draft/role-based redirect.
    }
    // Bind the upcoming callback to this browser so a phished callback URL
    // carrying a foreign token can't be silently exchanged (login-CSRF).
    markOauthFlowStarted();
    logger.debug("[auth] starting OAuth", provider, "returnTo:", returnTo);
    window.location.assign(`/api/auth/oauth/${provider}`);
  }

  // Pending / errored / no provider enabled → render nothing (divider too).
  if (providers.length === 0) {
    return null;
  }

  return (
    <div className="mb-5 space-y-4">
      {providers.includes("google") && (
        <div className="space-y-2">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => start("google")}
          >
            <GoogleLogo />
            <img src="/brand/favicon.svg" alt="" className="h-4 w-4" />
            Continue with Google
          </Button>
          <p className="text-muted text-center text-xs">
            For Board of Innovation employees: use your BOI Google account.
          </p>
        </div>
      )}
      <div className="flex items-center gap-3">
        <span className="bg-border h-px flex-1" />
        <span className="text-muted text-xs tracking-wide uppercase">or</span>
        <span className="bg-border h-px flex-1" />
      </div>
      <p className="text-muted text-center text-xs">
        External collaborators: sign in with the email and password below.
      </p>
    </div>
  );
}

function GoogleLogo() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.02-3.7H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.98 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.02-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.02 2.33C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}
